// ==============================
// ✅ MyResults.jsx (FULL UI CODE - FINAL UPDATE)
// ✅ MARKS + GRADE in SAME CARD (single marksheet card)
// ✅ Table header color changed from BLACK to PRIMARY (Bootstrap primary: #0d6efd)
// ✅ Final Summary also inside SAME CARD at the end
// ✅ PDF remains same (your PDF already perfect)
// ✅ Uses external CSS: ./MyResults.css
// ==============================

import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import useVendorBranch from '../../utils/useVendorBranch';

import './MyResults.css';

// ✅ React PDF
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  PDFDownloadLink,
} from '@react-pdf/renderer';

// ---------------------------
// helpers
// ---------------------------
const safe = (v) => (v === null || v === undefined ? '' : String(v));
const calcTotal = (rows, field) =>
  (rows || []).reduce((a, b) => a + (Number(b?.[field]) || 0), 0);
const calcPct = (obt, max) => {
  if (!max) return 0;
  return (obt / max) * 100;
};
const upper = (v) => safe(v).toUpperCase();

// ---------------------------
// PDF styles (UNCHANGED)
// ---------------------------
const pdfStyles = StyleSheet.create({
  page: { padding: 20, fontSize: 10 },

  /* ✅ PRIMARY TITLE BAR */
  titleBar: {
    backgroundColor: '#0d6efd',
    padding: 10,
    marginBottom: 10,
  },
  title: { fontSize: 14, fontWeight: 700, color: '#fff' },
  subTitle: { fontSize: 9, color: '#eaeaea', marginTop: 3 },

  infoBox: { borderWidth: 1, borderColor: '#ccc' },
  infoRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
  },
  infoCell: { flex: 1, padding: 8 },

  label: { fontSize: 9, fontWeight: 700 },
  value: { fontSize: 9 },

  sectionTitle: {
    marginTop: 12,
    marginBottom: 6,
    fontSize: 11,
    fontWeight: 700,
  },

  /* ✅ PRIMARY TABLE HEADER */
  table: { borderWidth: 1, borderColor: '#ccc' },
  tr: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd' },
  th: { backgroundColor: '#0d6efd' },
  thText: { color: '#fff', fontWeight: 700 },
  td: { padding: 7, fontSize: 9 },

  w60: { width: 60 },
  wFlex: { flex: 1 },
  w120: { width: 120 },
  w140: { width: 140 },

  footerBox: { borderWidth: 1, borderColor: '#ccc', marginTop: 0 },
  footerRow: { flexDirection: 'row' },
  footerCell: { flex: 1, padding: 8, fontSize: 9 },
});
// ---------------------------
// PDF doc (UNCHANGED)
// ---------------------------
const MyResultsPdfDoc = ({ header = {}, summary = [], details = [] }) => {
  const marksDetails = (details || []).filter(
    (x) => upper(x?.evaluated_by) === 'MARKS',
  );
  const gradeDetails = (details || []).filter(
    (x) => upper(x?.evaluated_by) === 'GRADE',
  );

  const totalMax = calcTotal(marksDetails, 'max_marks');
  const totalObt = calcTotal(marksDetails, 'obtained_marks');
  const pct = calcPct(totalObt, totalMax);
  const resultStatus =
    summary?.[0]?.result_status || (pct >= 35 ? 'PASS' : 'FAIL');

  const Line = ({ label, value }) => (
    <Text>
      <Text style={pdfStyles.label}>{label} </Text>
      <Text style={pdfStyles.value}>{safe(value) || '-'}</Text>
    </Text>
  );

  return (
    <Document>
      <Page size='A4' style={pdfStyles.page}>
        <View style={pdfStyles.titleBar}>
          <Text style={pdfStyles.title}>MARKSHEET</Text>
          <Text style={pdfStyles.subTitle}>
            {safe(header?.school_name) || 'Result'}
          </Text>
        </View>

        <View style={pdfStyles.infoBox}>
          <View style={pdfStyles.infoRow}>
            <View style={pdfStyles.infoCell}>
              <Line label='Student’s Name :' value={header?.student_name} />
            </View>
            <View style={pdfStyles.infoCell}>
              <Line label='Class :' value={header?.course_name} />
            </View>
          </View>

          <View style={pdfStyles.infoRow}>
            <View style={pdfStyles.infoCell}>
              <Line label='Father’s Name :' value={header?.f_name} />
            </View>
            <View style={pdfStyles.infoCell}>
              <Text>
                <Text style={pdfStyles.label}>Enrollment No : </Text>
                <Text style={pdfStyles.value}>
                  {safe(header?.enrollment_number) ||
                    safe(header?.enrollment_id) ||
                    '-'}
                </Text>
              </Text>
            </View>
          </View>

          <View style={pdfStyles.infoRow}>
            <View style={pdfStyles.infoCell}>
              <Line label='DOB :' value={header?.d_o_b} />
            </View>
            <View style={pdfStyles.infoCell}>
              <Line label='Academic Year :' value={header?.session_year} />
            </View>
          </View>

          <View style={pdfStyles.infoRow}>
            <View style={pdfStyles.infoCell}>
              <Line label='Stream :' value={header?.stream} />
            </View>
            <View style={pdfStyles.infoCell}>
              <Line label='Combination :' value={header?.combination} />
            </View>
          </View>

          <View style={pdfStyles.infoRow}>
            <View style={pdfStyles.infoCell}>
              <Line label='Language One :' value={header?.language_one} />
            </View>
            <View style={pdfStyles.infoCell}>
              <Line label='Exam Type :' value={header?.exam_type} />
            </View>
          </View>
        </View>

        <Text style={pdfStyles.sectionTitle}>Subjects (MARKS)</Text>
        <View style={pdfStyles.table}>
          <View style={[pdfStyles.tr, pdfStyles.th]}>
            <Text style={[pdfStyles.td, pdfStyles.w60, pdfStyles.thText]}>
              SN
            </Text>
            <Text style={[pdfStyles.td, pdfStyles.wFlex, pdfStyles.thText]}>
              SUBJECTS
            </Text>
            <Text style={[pdfStyles.td, pdfStyles.w140, pdfStyles.thText]}>
              MAXIMUM MARKS
            </Text>
            <Text style={[pdfStyles.td, pdfStyles.w140, pdfStyles.thText]}>
              OBTAINED MARKS
            </Text>
          </View>

          {marksDetails.length ? (
            marksDetails.map((x, i) => (
              <View style={pdfStyles.tr} key={x?.id || i}>
                <Text style={[pdfStyles.td, pdfStyles.w60]}>{i + 1}</Text>
                <Text style={[pdfStyles.td, pdfStyles.wFlex]}>
                  {safe(x?.subject_name) || '-'}
                </Text>
                <Text style={[pdfStyles.td, pdfStyles.w140]}>
                  {safe(x?.max_marks) || '-'}
                </Text>
                <Text style={[pdfStyles.td, pdfStyles.w140]}>
                  {safe(x?.obtained_marks) || '-'}
                </Text>
              </View>
            ))
          ) : (
            <View style={pdfStyles.tr}>
              <Text style={[pdfStyles.td, pdfStyles.wFlex]}>
                No MARKS records
              </Text>
            </View>
          )}
        </View>

        {gradeDetails.length > 0 ? (
          <>
            <Text style={pdfStyles.sectionTitle}>Subjects (GRADE)</Text>
            <View style={pdfStyles.table}>
              <View style={[pdfStyles.tr, pdfStyles.th]}>
                <Text style={[pdfStyles.td, pdfStyles.w60, pdfStyles.thText]}>
                  SN
                </Text>
                <Text style={[pdfStyles.td, pdfStyles.wFlex, pdfStyles.thText]}>
                  SUBJECTS
                </Text>
                <Text style={[pdfStyles.td, pdfStyles.w120, pdfStyles.thText]}>
                  PASS GRADE
                </Text>
                <Text style={[pdfStyles.td, pdfStyles.w120, pdfStyles.thText]}>
                  OBT GRADE
                </Text>
              </View>

              {gradeDetails.map((x, i) => (
                <View style={pdfStyles.tr} key={x?.id || `g-${i}`}>
                  <Text style={[pdfStyles.td, pdfStyles.w60]}>{i + 1}</Text>
                  <Text style={[pdfStyles.td, pdfStyles.wFlex]}>
                    {safe(x?.subject_name) || '-'}
                  </Text>
                  <Text style={[pdfStyles.td, pdfStyles.w120]}>
                    {safe(x?.passing_grade) || '-'}
                  </Text>
                  <Text style={[pdfStyles.td, pdfStyles.w120]}>
                    {safe(x?.obtained_grade) || '-'}
                  </Text>
                </View>
              ))}
            </View>
          </>
        ) : null}

        <Text style={pdfStyles.sectionTitle}>Final Result Summary</Text>
        <View style={pdfStyles.footerBox}>
          <View style={pdfStyles.footerRow}>
            <Text style={pdfStyles.footerCell}>
              Marks Obtained : {safe(totalObt)} Out Of {safe(totalMax)}
            </Text>
          </View>
          <View style={pdfStyles.footerRow}>
            <Text style={pdfStyles.footerCell}>
              Percentage : {pct.toFixed(2)}%
            </Text>
            <Text style={pdfStyles.footerCell}>
              Result : {safe(resultStatus)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// ---------------------------
// MAIN COMPONENT
// ---------------------------
const MyResults = () => {
  const { user } = useSelector((state) => state.authState);
  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [header, setHeader] = useState({});
  const [summary, setSummary] = useState([]);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });
  const [pdfOpen, setPdfOpen] = useState(false);

  const marksDetails = useMemo(
    () => (details || []).filter((x) => upper(x?.evaluated_by) === 'MARKS'),
    [details],
  );
  const gradeDetails = useMemo(
    () => (details || []).filter((x) => upper(x?.evaluated_by) === 'GRADE'),
    [details],
  );

  const totalMax = useMemo(
    () => calcTotal(marksDetails, 'max_marks'),
    [marksDetails],
  );
  const totalObt = useMemo(
    () => calcTotal(marksDetails, 'obtained_marks'),
    [marksDetails],
  );
  const pct = useMemo(() => calcPct(totalObt, totalMax), [totalObt, totalMax]);

  const resultStatus = useMemo(() => {
    return summary?.[0]?.result_status || (pct >= 35 ? 'PASS' : 'FAIL');
  }, [summary, pct]);

  const GetMyResults = async () => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      setLoading(true);

      const res = await apiService.get(
        `api/BatchExamMaster/GetMyResults?vendorId=${vendorId}&branchId=${branchId}`,
      );

      const ok = res?.status === true || res?.Status === true;
      const message = res?.message || res?.Message || 'Failed';

      const hdr = res?.header || res?.Header || {};
      const sum = res?.summary || res?.Summary || [];
      const det = res?.details || res?.Details || [];

      if (ok) {
        setHeader(hdr || {});
        setSummary(Array.isArray(sum) ? sum : []);
        setDetails(Array.isArray(det) ? det : []);
        if (message) toast.info(message);
      } else {
        toast.error(message || 'Failed');
        setHeader({});
        setSummary([]);
        setDetails([]);
      }
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong');
      setHeader({});
      setSummary([]);
      setDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) GetMyResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData.vendor_id, selectionData.branch_id]);

  const disablePdf = loading || (summary.length === 0 && details.length === 0);

  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      {/* PDF buttons */}
      <div className='d-flex justify-content-end gap-2 mb-2'>
        <Button
          color='primary'
          size='sm'
          onClick={() => setPdfOpen(true)}
          disabled={disablePdf}
          title={disablePdf ? 'No data' : 'Open PDF Viewer'}
        >
          Open PDF
        </Button>

        <PDFDownloadLink
          document={
            <MyResultsPdfDoc
              header={header}
              summary={summary}
              details={details}
            />
          }
          fileName='MyResults.pdf'
        >
          {({ loading: pdfLoading }) => (
            <Button
              color='success'
              size='sm'
              disabled={disablePdf || pdfLoading}
            >
              {pdfLoading ? 'Preparing...' : 'Download PDF'}
            </Button>
          )}
        </PDFDownloadLink>
      </div>

      {/* ✅ ONE SINGLE CARD: HEADER + MARKS + GRADE + SUMMARY */}
      <Card className='marksheet-card mb-3'>
        <CardBody className='p-0'>
          {/* title */}
          <div className='ms-titlebar'>
            <div className='ms-title'>MARKSHEET</div>
            <div className='ms-subtitle'>{header?.school_name || 'Result'}</div>
          </div>

          {/* header info */}
          <div className='marksheet-top'>
            <div className='ms-row'>
              <div className='ms-cell'>
                <span className='ms-label'>Student’s Name :</span>
                <span className='ms-value'>{header?.student_name || '-'}</span>
              </div>
              <div className='ms-cell'>
                <span className='ms-label'>Class :</span>
                <span className='ms-value'>{header?.course_name || '-'}</span>
              </div>
            </div>

            <div className='ms-row'>
              <div className='ms-cell'>
                <span className='ms-label'>Father’s Name :</span>
                <span className='ms-value'>{header?.f_name || '-'}</span>
              </div>
              <div className='ms-cell'>
                <span className='ms-label'>Enrollment No :</span>
                <span className='ms-value'>
                  {header?.enrollment_number || header?.enrollment_id || '-'}
                </span>
              </div>
            </div>

            <div className='ms-row'>
              <div className='ms-cell'>
                <span className='ms-label'>DOB :</span>
                <span className='ms-value'>{header?.d_o_b || '-'}</span>
              </div>
              <div className='ms-cell'>
                <span className='ms-label'>Academic Year :</span>
                <span className='ms-value'>{header?.session_year || '-'}</span>
              </div>
            </div>

            <div className='ms-row'>
              <div className='ms-cell'>
                <span className='ms-label'>Exam Type :</span>
                <span className='ms-value'>{header?.exam_type || '-'}</span>
              </div>
              {/* <div className="ms-cell">
                <span className="ms-label">Language One :</span>
                <span className="ms-value">{header?.language_one || '-'}</span>
              </div> */}
            </div>
          </div>

          {/* body */}
          <div className='marksheet-body'>
            {/* MARKS */}
            <div className='ms-section-title'>Subjects (MARKS)</div>
            <div className='ms-table ms-marks-grid'>
              <div className='ms-thead'>
                <div className='ms-th'>SN</div>
                <div className='ms-th'>SUBJECTS</div>
                <div className='ms-th'>MAXIMUM MARKS</div>
                <div className='ms-th'>OBTAINED MARKS</div>
              </div>

              {marksDetails?.length ? (
                marksDetails.map((x, i) => (
                  <div className='ms-tr' key={x?.id || i}>
                    <div className='ms-td'>{i + 1}</div>
                    <div className='ms-td'>{x?.subject_name || '-'}</div>
                    <div className='ms-td'>{x?.max_marks ?? '-'}</div>
                    <div className='ms-td'>{x?.obtained_marks ?? '-'}</div>
                  </div>
                ))
              ) : (
                <div className='ms-tr'>
                  <div className='ms-td ms-empty'>No MARKS records</div>
                </div>
              )}
            </div>

            {/* GRADE - same card */}
            {gradeDetails.length > 0 ? (
              <>
                <div className='ms-section-title mt-4'>Subjects (GRADE)</div>

                <div className='ms-table ms-grade-grid'>
                  <div className='ms-thead'>
                    <div className='ms-th'>SN</div>
                    <div className='ms-th'>SUBJECTS</div>
                    <div className='ms-th'>PASS GRADE</div>
                    <div className='ms-th'>OBT GRADE</div>
                  </div>

                  {gradeDetails.map((x, i) => (
                    <div className='ms-tr' key={x?.id || `g-${i}`}>
                      <div className='ms-td'>{i + 1}</div>
                      <div className='ms-td'>{x?.subject_name || '-'}</div>
                      <div className='ms-td'>{x?.passing_grade ?? '-'}</div>
                      <div className='ms-td'>{x?.obtained_grade ?? '-'}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {/* Final Summary - same card, end */}
            {(marksDetails.length > 0 || gradeDetails.length > 0) && (
              <>
                <div className='ms-section-title mt-4'>
                  Final Result Summary
                </div>

                <div className='ms-summary-box'>
                  <div className='ms-summary-row one'>
                    <div className='ms-summary-cell'>
                      Marks Obtained : <b>{safe(totalObt)}</b> Out Of{' '}
                      <b>{safe(totalMax)}</b>
                    </div>
                  </div>

                  <div className='ms-summary-row two'>
                    <div className='ms-summary-cell'>
                      Percentage : <b>{pct.toFixed(2)}%</b>
                    </div>
                    <div className='ms-summary-cell'>
                      Result :{' '}
                      <b
                        className={
                          String(resultStatus).toUpperCase().includes('PASS')
                            ? 'ms-pass'
                            : 'ms-fail'
                        }
                      >
                        {resultStatus}
                      </b>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      {/* PDF Viewer Modal */}
      <Modal
        isOpen={pdfOpen}
        toggle={() => setPdfOpen(false)}
        size='xl'
        style={{ maxWidth: '1100px' }}
      >
        <ModalHeader toggle={() => setPdfOpen(false)}>
          MyResults - PDF Preview
        </ModalHeader>
        <ModalBody style={{ height: '80vh' }}>
          <PDFViewer style={{ width: '100%', height: '100%' }}>
            <MyResultsPdfDoc
              header={header}
              summary={summary}
              details={details}
            />
          </PDFViewer>
        </ModalBody>
      </Modal>
    </>
  );
};

export default MyResults;
